/**
 * Help articles, one locale at a time (#366).
 *
 * The articles used to be one static import of every article in every locale
 * (1.7 MB, 321 kB gzip on every help route). Now each locale is its own chunk
 * (`generated/articles.<locale>.json`, written by scripts/generate-articles.js
 * and the help-center Vite plugin), loaded by the help routes' loaders in
 * navigation/help/routes.ts before the route renders. The hooks then read it
 * synchronously, so a help page never renders a half-loaded state — and the
 * prerenderer never captures one.
 *
 * Reading a locale that was not loaded throws: it means a route lost its
 * loader, and a loud failure beats a silent "article not found".
 */
import {useSyncExternalStore} from 'react';
import {Article} from './types';

export interface LocaleArticles {
  locale: string;
  /** The locale's own articles */
  articles: Article[];
  /** English articles for the slugs the locale lacks (all of them if none) */
  fallback: Article[];
  /** slug -> locales that have it */
  availability: Record<string, string[]>;
}

const bundleLoaders = import.meta.glob<LocaleArticles>(
  './generated/articles.*.json',
  {import: 'default'},
);

const loaded = new Map<string, LocaleArticles>();
const pending = new Map<string, Promise<LocaleArticles>>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

/** Loads (once) and caches one locale's articles. */
export function loadHelpArticles(locale: string): Promise<LocaleArticles> {
  const ready = loaded.get(locale);
  if (ready) {
    return Promise.resolve(ready);
  }
  const inFlight = pending.get(locale);
  if (inFlight) {
    return inFlight;
  }
  const loader = bundleLoaders[`./generated/articles.${locale}.json`];
  if (!loader) {
    return Promise.reject(
      new Error(`No help article bundle for locale "${locale}"`),
    );
  }
  const promise = loader()
    .then((bundle) => {
      loaded.set(locale, bundle);
      return bundle;
    })
    .finally(() => {
      // A failed load leaves no trace, so the next navigation retries it
      pending.delete(locale);
      notify();
    });
  pending.set(locale, promise);
  notify();
  return promise;
}

/** One locale's articles; throws if its route did not load them first. */
export function getHelpArticles(locale: string): LocaleArticles {
  const bundle = loaded.get(locale);
  if (!bundle) {
    throw new Error(
      `Help articles for "${locale}" are not loaded — every help route must ` +
        'run loadHelpArticles in its loader (navigation/help/routes.ts).',
    );
  }
  return bundle;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const isLoading = () => pending.size > 0;

/** True while a locale's articles are being fetched (drives aria-busy). */
export function useHelpArticlesLoading(): boolean {
  return useSyncExternalStore(subscribe, isLoading, isLoading);
}

/** Test-only: put a locale's articles in place without a dynamic import. */
export function setHelpArticlesForTests(bundle: LocaleArticles): void {
  loaded.set(bundle.locale, bundle);
}

/** Test-only: forget every loaded and in-flight locale. */
export function resetHelpArticlesForTests(): void {
  loaded.clear();
  pending.clear();
  notify();
}
